/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import { useTranslation } from 'react-i18next'
import useMediaQuery from 'beautiful-react-hooks/useMediaQuery'
import { MOBILE_THRESHOLD } from '../../constants'
import useUser from '../Shared/UserProvider/useUser'
import OrderStore from '../Orders/OrderStore'
import { useMutation, useQuery } from '@tanstack/react-query'
import { getMerchantStatus, setBusyMode } from '../../services/PointOfSale/PointOfSale.service'
import { isProviderAPos } from '../../services/Provider/Provider.service'
import { Button, Icon, Message, MessageContent, MessageHeader } from 'semantic-ui-react'
import humanizeDuration from '../../helpers/humanizeDuration'
import { TIME } from '../../services/date.service'
import i18n from 'i18next'
import { useEffect } from 'react'
import { ultyToast } from '../Shared/UltyToast/UltyToast'

const BusyModeMessage = () => {
    const [t] = useTranslation()
    const isMobile = useMediaQuery(`(max-width: ${MOBILE_THRESHOLD}px)`)
    const {user} = useUser()
    const orderStore = OrderStore.useContainer()

    const updateBusyModeMutation = useMutation({
        mutationFn: () => setBusyMode({status: orderStore.pausedAt ? 'IDLE' : 'BUSY'}),
        onSuccess: (data) => {
            orderStore.setPausedAt(data.pausedAt)
            orderStore.setPausedUntil(data.pausedUntil)
        },
        onError: () => {
            ultyToast({ type: 'error', title: t('global.anErrorOccurred') })
        },
    })

    const pauseDuration = orderStore.pausedAt ? new Date() - new Date(orderStore.pausedAt) : 0
    const lessThan1Minute = pauseDuration < TIME.MINUTE_IN_MS
    const lessThan48Hours = pauseDuration < 48 * TIME.HOUR_IN_MS

    const {data: posStatus, isSuccess} = useQuery({
        queryKey: ['pointofsales', 'busy'],
        queryFn: getMerchantStatus,
    })

    useEffect(() => {
        if (isSuccess) {
            orderStore.setPausedAt(posStatus.pausedAt)
        }
    }, [isSuccess, posStatus])

    const busyModeFinished = orderStore.pausedUntil && new Date(orderStore.pausedUntil) <= new Date()
    if (!isProviderAPos(user?.provider) || !orderStore.pausedAt || busyModeFinished) {
        return null
    }

    const getHeaderMessage = () => {
        if (lessThan1Minute) {
            return t('navbar.busy_mode.busy_mode')
        }
        if (lessThan48Hours) {
            return t('navbar.busy_mode.busy_mode_since_duration', {
                duration: humanizeDuration(new Date(), orderStore.pausedAt, {
                    round: true,
                    units: ['h', 'm'],
                    largest: 2,
                }),
            })
        }
        return t('navbar.busy_mode.busy_mode_since_date', {
            date: new Intl.DateTimeFormat(i18n.language, {
                day: 'numeric',
                month: 'long',
                year: 'numeric',
                hour: 'numeric',
                minute: 'numeric',
            }).format(new Date(orderStore.pausedAt)),
        })
    }

    const getEndDuration = () => {
        if (!orderStore.pausedUntil) return null

        const pausedUntilDate = new Date(orderStore.pausedUntil)
        const now = new Date()
        if (pausedUntilDate <= now) return null

        const isToday = pausedUntilDate.toDateString() === now.toDateString()

        if (!isToday) {
            const dateStr = new Intl.DateTimeFormat(i18n.language, {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
            }).format(pausedUntilDate)
            const timeStr = new Intl.DateTimeFormat(i18n.language, {
                hour: '2-digit',
                minute: '2-digit',
            }).format(pausedUntilDate)
            return t('navbar.busy_mode.end_busy_mode_on', {
                date: t('navbar.busy_mode.date_at_time', { date: dateStr, time: timeStr }),
            })
        }

        return t('navbar.busy_mode.end_busy_mode_in', {
            duration: humanizeDuration(pausedUntilDate, now, {
                round: true,
                units: ['h', 'm'],
                largest: 2,
            }),
        })
    }

    const endDuration = getEndDuration()

    return (
        <div>
            <Message
                icon
                attached
                color="orange"
                size="small"
            >
                <Icon name="warning sign"/>
                <MessageContent>
                    <MessageHeader>
                        {getHeaderMessage()}
                    </MessageHeader>
                    <span css={css`
                        display: block;
                        margin-top: 0.25rem;
                        opacity: 0.85;
                        font-size: 0.9em;
                    `}>
                        {t('navbar.busy_mode.closed_on')}
                    </span>
                </MessageContent>
            </Message>
            <Message
                attached="bottom"
                color="orange"
                size="small"
                css={css`
                    &.ui.bottom.attached.message {
                        padding-top: 0.6rem;
                        padding-bottom: 0.6rem;
                        padding-left: 1.5rem;
                        padding-right: 1.5rem;
                        box-shadow: -1px -1px 0 0 #f2711c inset,
                            1px -1px 0 0 #f2711c inset,
                            0 -1px 0 0 #f2711c inset;
                    }
                `}
            >
                <div css={css`
                    display: flex;
                    flex-direction: ${isMobile ? 'column' : 'row'};
                    align-items: ${isMobile ? 'flex-start' : 'center'};
                    justify-content: space-between;
                    gap: 0.6rem;
                `}>
                    {endDuration && (
                        <span css={css`
                            font-weight: 600;
                            font-size: 0.9em;
                        `}>
                            {endDuration}
                        </span>
                    )}
                    <div css={css`
                        ${!endDuration ? 'margin-left: auto;' : ''}
                        ${isMobile ? 'width: 100%;' : ''}
                    `}>
                        <Button
                            size="tiny"
                            fluid={isMobile}
                            color="orange"
                            onClick={updateBusyModeMutation.mutate}
                            disabled={updateBusyModeMutation.isPending}
                            loading={updateBusyModeMutation.isPending}
                        >
                            {t('navbar.busy_mode.stop_busy_mode')}
                        </Button>
                    </div>
                </div>
            </Message>
        </div>
    )
}

export { BusyModeMessage }
